
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {ClientReportsGetResponse} from './ClientReportsGetResponse';
import {ClientReportType} from '../core/ClientReportType';
import {FieldModelClientReportType} from '../core/ClientReportType';
import {RequestDirection} from '../core/RequestDirection';
import {FieldModelRequestDirection} from '../core/RequestDirection';

/**
 * ClientReportsGetRequest struct
 */
class ClientReportsGetRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {number=} sinceRecordId
   * @param {Date=} timeFrom
   * @param {Date=} timeTo
   * @param {number=} accountLogin
   * @param {!Set=} clientReportTypes
   * @param {!RequestDirection=} requestDirection
   * @param {number=} limit
   * @constructor
   */
  constructor (parent = new Request(), sinceRecordId = null, timeFrom = null, timeTo = null, accountLogin = null, clientReportTypes = new Set(), requestDirection = new RequestDirection(RequestDirection.FORWARD), limit = null) {
    super()
    super.copy(parent)
    this.SinceRecordId = sinceRecordId
    this.TimeFrom = timeFrom
    this.TimeTo = timeTo
    this.AccountLogin = accountLogin
    this.ClientReportTypes = clientReportTypes
    this.RequestDirection = requestDirection
    this.Limit = limit
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ClientReportsGetRequest}
   * @param {!ClientReportsGetRequest} other Other struct
   * @returns {!ClientReportsGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SinceRecordId != null) {
      this.SinceRecordId = UInt64.fromNumber(other.SinceRecordId)
    } else {
      this.SinceRecordId = null
    }
    if (other.TimeFrom != null) {
      if (other.TimeFrom instanceof Date) {
        this.TimeFrom = new Date(other.TimeFrom.getTime())
      } else {
        this.TimeFrom = new Date(Math.round(other.TimeFrom / 1000000))
      }
    } else {
      this.TimeFrom = null
    }
    if (other.TimeTo != null) {
      if (other.TimeTo instanceof Date) {
        this.TimeTo = new Date(other.TimeTo.getTime())
      } else {
        this.TimeTo = new Date(Math.round(other.TimeTo / 1000000))
      }
    } else {
      this.TimeTo = null
    }
    if (other.AccountLogin != null) {
      this.AccountLogin = UInt64.fromNumber(other.AccountLogin)
    } else {
      this.AccountLogin = null
    }
    if (other.ClientReportTypes != null) {
      this.ClientReportTypes = new Set()
      for (let item of other.ClientReportTypes) {
        if (item != null) {
          let tempItem
          tempItem = ClientReportType.fromObject(item)
          this.ClientReportTypes.add(tempItem)
        } else {
          this.ClientReportTypes.add(null)
        }
      }
    } else {
      this.ClientReportTypes = null
    }
    if (other.RequestDirection != null) {
      this.RequestDirection = RequestDirection.fromObject(other.RequestDirection)
    } else {
      this.RequestDirection = null
    }
    if (other.Limit != null) {
      this.Limit = UInt64.fromNumber(other.Limit)
    } else {
      this.Limit = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ClientReportsGetRequest}
   * @returns {!ClientReportsGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ClientReportsGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ClientReportsGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ClientReportsGetRequest}
   * @param {!ClientReportsGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ClientReportsGetRequest)) {
      throw new TypeError('Instance of ClientReportsGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ClientReportsGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SinceRecordId: ((this.SinceRecordId != null) ? this.SinceRecordId.toNumber() : null),
      TimeFrom: ((this.TimeFrom != null) ? (this.TimeFrom.getTime() * 1000000) : null),
      TimeTo: ((this.TimeTo != null) ? (this.TimeTo.getTime() * 1000000) : null),
      AccountLogin: ((this.AccountLogin != null) ? this.AccountLogin.toNumber() : null),
      ClientReportTypes: ((this.ClientReportTypes != null) ? Array.from(this.ClientReportTypes, item => ((item != null) ? item : null)) : null),
      RequestDirection: ((this.RequestDirection != null) ? this.RequestDirection : null),
      Limit: ((this.Limit != null) ? this.Limit.toNumber() : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ClientReportsGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ClientReportsGetRequest} other Object value
   * @returns {!ClientReportsGetRequest} Created struct
   */
  static fromObject (other) {
    return new ClientReportsGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ClientReportsGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ClientReportsGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ClientReportsGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3028
  }
}

export { ClientReportsGetRequest };

/**
 * Fast Binary Encoding ClientReportsGetRequest field model
 */
class FieldModelClientReportsGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.SinceRecordId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.TimeFrom = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, this.SinceRecordId.fbeOffset + this.SinceRecordId.fbeSize), buffer, this.SinceRecordId.fbeOffset + this.SinceRecordId.fbeSize)
    this.TimeTo = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, this.TimeFrom.fbeOffset + this.TimeFrom.fbeSize), buffer, this.TimeFrom.fbeOffset + this.TimeFrom.fbeSize)
    this.AccountLogin = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.TimeTo.fbeOffset + this.TimeTo.fbeSize), buffer, this.TimeTo.fbeOffset + this.TimeTo.fbeSize)
    this.ClientReportTypes = new fbe.FieldModelSet(new FieldModelClientReportType(buffer, this.AccountLogin.fbeOffset + this.AccountLogin.fbeSize), buffer, this.AccountLogin.fbeOffset + this.AccountLogin.fbeSize)
    this.RequestDirection = new FieldModelRequestDirection(buffer, this.ClientReportTypes.fbeOffset + this.ClientReportTypes.fbeSize)
    this.Limit = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.RequestDirection.fbeOffset + this.RequestDirection.fbeSize), buffer, this.RequestDirection.fbeOffset + this.RequestDirection.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SinceRecordId.fbeSize + this.TimeFrom.fbeSize + this.TimeTo.fbeSize + this.AccountLogin.fbeSize + this.ClientReportTypes.fbeSize + this.RequestDirection.fbeSize + this.Limit.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SinceRecordId.fbeExtra + this.TimeFrom.fbeExtra + this.TimeTo.fbeExtra + this.AccountLogin.fbeExtra + this.ClientReportTypes.fbeExtra + this.RequestDirection.fbeExtra + this.Limit.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelClientReportsGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3028
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SinceRecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SinceRecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SinceRecordId.fbeSize

    if ((fbeCurrentSize + this.TimeFrom.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimeFrom.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeFrom.fbeSize

    if ((fbeCurrentSize + this.TimeTo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimeTo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeTo.fbeSize

    if ((fbeCurrentSize + this.AccountLogin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountLogin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountLogin.fbeSize

    if ((fbeCurrentSize + this.ClientReportTypes.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ClientReportTypes.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientReportTypes.fbeSize

    if ((fbeCurrentSize + this.RequestDirection.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RequestDirection.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestDirection.fbeSize

    if ((fbeCurrentSize + this.Limit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Limit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Limit.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!ClientReportsGetRequest} fbeValue Default value, defaults is new ClientReportsGetRequest()
   * @returns {!ClientReportsGetRequest} ClientReportsGetRequest value
   */
  get (fbeValue = new ClientReportsGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!ClientReportsGetRequest} fbeValue ClientReportsGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SinceRecordId.fbeSize) <= fbeStructSize) {
      fbeValue.SinceRecordId = this.SinceRecordId.get()
    } else {
      fbeValue.SinceRecordId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SinceRecordId.fbeSize

    if ((fbeCurrentSize + this.TimeFrom.fbeSize) <= fbeStructSize) {
      fbeValue.TimeFrom = this.TimeFrom.get()
    } else {
      fbeValue.TimeFrom = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeFrom.fbeSize

    if ((fbeCurrentSize + this.TimeTo.fbeSize) <= fbeStructSize) {
      fbeValue.TimeTo = this.TimeTo.get()
    } else {
      fbeValue.TimeTo = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeTo.fbeSize

    if ((fbeCurrentSize + this.AccountLogin.fbeSize) <= fbeStructSize) {
      fbeValue.AccountLogin = this.AccountLogin.get()
    } else {
      fbeValue.AccountLogin = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountLogin.fbeSize

    if ((fbeCurrentSize + this.ClientReportTypes.fbeSize) <= fbeStructSize) {
      this.ClientReportTypes.get(fbeValue.ClientReportTypes)
    } else {
      fbeValue.ClientReportTypes.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientReportTypes.fbeSize

    if ((fbeCurrentSize + this.RequestDirection.fbeSize) <= fbeStructSize) {
      fbeValue.RequestDirection = this.RequestDirection.get(new RequestDirection(RequestDirection.FORWARD))
    } else {
      fbeValue.RequestDirection = new RequestDirection(RequestDirection.FORWARD)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestDirection.fbeSize

    if ((fbeCurrentSize + this.Limit.fbeSize) <= fbeStructSize) {
      fbeValue.Limit = this.Limit.get()
    } else {
      fbeValue.Limit = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Limit.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelClientReportsGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelClientReportsGetRequest}
   * @param {!ClientReportsGetRequest} fbeValue ClientReportsGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelClientReportsGetRequest}
   * @param {ClientReportsGetRequest} fbeValue ClientReportsGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SinceRecordId.set(fbeValue.SinceRecordId)
    this.TimeFrom.set(fbeValue.TimeFrom)
    this.TimeTo.set(fbeValue.TimeTo)
    this.AccountLogin.set(fbeValue.AccountLogin)
    this.ClientReportTypes.set(fbeValue.ClientReportTypes)
    this.RequestDirection.set(fbeValue.RequestDirection)
    this.Limit.set(fbeValue.Limit)
  }
}

export { FieldModelClientReportsGetRequest };

/**
 * Fast Binary Encoding ClientReportsGetRequest model
 */
class ClientReportsGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelClientReportsGetRequest(this.buffer, 4)
  }

  /**
   * Get the ClientReportsGetRequest model
   * @this {!ClientReportsGetRequestModel}
   * @returns {!FieldModelClientReportsGetRequest} model ClientReportsGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ClientReportsGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ClientReportsGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ClientReportsGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ClientReportsGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelClientReportsGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ClientReportsGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ClientReportsGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ClientReportsGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ClientReportsGetRequestModel}
   * @param {!ClientReportsGetRequest} value ClientReportsGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ClientReportsGetRequestModel}
   * @param {!ClientReportsGetRequest} value ClientReportsGetRequest value, defaults is new ClientReportsGetRequest()
   * @return {!object} Deserialized ClientReportsGetRequest value and its size
   */
  deserialize (value = new ClientReportsGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ClientReportsGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ClientReportsGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ClientReportsGetRequestModel}
   * @param {!number} prev Previous ClientReportsGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ClientReportsGetRequestModel };
ClientReportsGetRequest.__has_response = true;
ClientReportsGetRequest.__response_class = ClientReportsGetResponse;
